import { Calendar } from "../ui/calendar";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import { FieldErrors, FieldProps } from "./field";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { ComponentProps, useRef, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { format, Locale } from "date-fns";
import { de } from "date-fns/locale";
import t from "@/lang/lang";
import { RFC3339timestamp } from "@bitory-gmbh/nidavellir";
import { DayPicker } from "react-day-picker";
import { Input } from "../ui/input";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";

export type DatePickerFieldProps =
  | {
      nullable: true;
      date: RFC3339timestamp | undefined;
      onDateChange: (date?: RFC3339timestamp) => void;
    }
  | {
      nullable: false;
      date: RFC3339timestamp;
      onDateChange: (date: RFC3339timestamp) => void;
    };

/*
 * DateField provices a single component
 * wrapping shadcn/ui Field components
 * to collect date input.
 *
 * It is limited to selecting a single date.
 * Ranges are not possible. Define new
 * component if needed.
 *
 * As a specialty, you must decide whether
 * the date value can be
 */
export function DatePickerField({
  label,
  description,
  errors,
  fieldProps,
  nullable,
  date,
  onDateChange,
  locale = de,
  weekStartsOn = 1,
  ...props
}: DatePickerFieldProps & {
  locale?: Locale;
} & Omit<ComponentProps<typeof DayPicker>, "mode" | "selected" | "onSelect"> &
  FieldProps) {
  return (
    <Field {...fieldProps}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            data-empty={!date}
            className="data-[empty=true]:text-muted-foreground justify-between text-left font-normal"
            aria-invalid={errors && errors.length > 0}
          >
            <CalendarIcon />
            {date ? (
              format(new Date(date), "PPP", { locale })
            ) : (
              <span>{t("Datum wählen")}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          {nullable ? (
            <Calendar
              {...props}
              mode="single"
              selected={date ? new Date(date) : undefined}
              onSelect={(newDate?: Date) => {
                onDateChange(
                  newDate ? setCurrentTime(newDate).toISOString() : undefined,
                );
              }}
              weekStartsOn={weekStartsOn}
            />
          ) : (
            <Calendar
              {...props}
              mode="single"
              selected={new Date(date)}
              onSelect={(newDate?: Date) =>
                onDateChange(
                  newDate ? setCurrentTime(newDate).toISOString() : date,
                )
              }
              weekStartsOn={weekStartsOn}
            />
          )}
        </PopoverContent>
      </Popover>
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}

function setCurrentTime(date: Date): Date {
  const now = new Date();
  date.setHours(now.getHours());
  date.setMinutes(now.getMinutes());
  return date;
}

export function DatetimePickerField({
  label,
  description,
  errors,
  fieldProps,
  nullable,
  date,
  onDateChange,
  disabled,
}: DatePickerFieldProps & {
  disabled?: boolean;
} & FieldProps) {
  // Local state to keep performant at all times.
  // Passing global state caused some issues
  // together with debounce.
  const [datetime, setDatetime] = useState(date ?? "");
  const focused = useRef(false);

  // Only sync if not focussed
  useRefEffect(date, () => {
    if (!focused.current) setDatetime(date ?? "");
  });

  return (
    <Field {...fieldProps}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <div className="grid grid-cols-4 gap-2">
        <div className="col-span-3">
          {nullable ? (
            <DatePickerField
              nullable={true}
              date={datetime}
              onDateChange={(newDate) => {
                if (newDate == null) onDateChange(newDate);
                const newDateWithTime = new Date(newDate!);
                const time = toTime(date);
                const [hours, minutes] = time.split(":");
                newDateWithTime.setHours(Number(hours), Number(minutes));
                const newDateWithTimeString = newDateWithTime.toISOString();
                setDatetime(newDateWithTimeString);
                onDateChange(newDateWithTimeString);
              }}
            />
          ) : (
            <DatePickerField
              nullable={false}
              date={datetime}
              onDateChange={(newDate) => {
                if (newDate == null) onDateChange(newDate);
                const newDateWithTime = new Date(newDate!);
                const time = toTime(date);
                const [hours, minutes] = time.split(":");
                newDateWithTime.setHours(Number(hours), Number(minutes));
                const newDateWithTimeString = newDateWithTime.toISOString();
                setDatetime(newDateWithTimeString);
                onDateChange(newDateWithTimeString);
              }}
            />
          )}
        </div>
        <Input
          type="time"
          id="time-picker"
          step="60"
          aria-invalid={errors && errors.length > 0}
          onFocus={() => {
            focused.current = true;
          }}
          value={toTime(datetime)}
          onChange={(e) => {
            const [hours, minutes] = e.target.value.split(":");
            const newDate = datetime ? new Date(datetime) : new Date();
            newDate.setHours(Number(hours), Number(minutes));
            const newDateString = newDate.toISOString();
            setDatetime(newDateString);
            onDateChange(newDateString);
          }}
          className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
          disabled={disabled}
        />
      </div>
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}

function toTime(date: RFC3339timestamp | undefined): string {
  if (!date) return "09:00"; // Default time if no date is provided
  const d = new Date(date);
  return d.toTimeString().split(" ")[0].substring(0, 5); // Returns time in HH:MM
}
